import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Button } from "@mui/material";
import Select from "react-select";
import ProteinPreference from '../ProteinPreference';
import TexturePreference from '../TexturePreference';
import { apiCall } from '../../services/apiService';

const Preferences = ({ formData = {}, loading = false, preferencesSubstep = 0, onFormChange, onNext }) => {
    const [productOptions, setProductOptions] = useState([]);
    
    // Make sure formData fields exist and are arrays
    const safeFormData = {
      likedProteins: Array.isArray(formData.likedProteins) ? formData.likedProteins : [],
      dislikedProteins: Array.isArray(formData.dislikedProteins) ? formData.dislikedProteins : [],
      likedTextures: Array.isArray(formData.likedTextures) ? formData.likedTextures : [],
      dislikedTextures: Array.isArray(formData.dislikedTextures) ? formData.dislikedTextures : [],
      preferredBrands: Array.isArray(formData.preferredBrands) ? formData.preferredBrands : [],
      dislikedBrands: Array.isArray(formData.dislikedBrands) ? formData.dislikedBrands : [],
      likedProducts: Array.isArray(formData.likedProducts) ? formData.likedProducts : [],
      dislikedProducts: Array.isArray(formData.dislikedProducts) ? formData.dislikedProducts : [],
      // Add the new exploration flags with defaults
      exploreNewProteins: formData.exploreNewProteins || false,
      exploreNewTextures: formData.exploreNewTextures || false,
      exploreNewBrands: formData.exploreNewBrands || false,
      ...formData
    };
    
    // Handle form field changes
    const handleChange = (field, value) => {
      if (onFormChange) {
        onFormChange(field, value);
      }
    };
    
    // Fetch products on component mount
    useEffect(() => {
      const fetchProducts = async () => {
        try {
          const response = await apiCall('api/products');
          if (response.ok) {
            const data = await response.json();
            setProductOptions(data || []);
          }
        } catch (error) {
          console.error("Error fetching products:", error);
          setProductOptions([]);
        }
      };
      
      fetchProducts();
    }, []);
    
    // Render the exploration checkbox
    const renderExplorationCheckbox = (field, label) => (
        <>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            width: '100%'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={safeFormData[field] || false}
                  onChange={(e) => handleChange(field, e.target.checked)}
                  color="primary"
                />
              }
              label={label}
                             sx={{ 
                 padding: '6px 10px',
                 border: '1px solid #e0e0e0',
                 borderRadius: '8px',
                 maxWidth: '85%',
                 margin: '0 auto',
                 mt: 0,
                 mb: 0,
                 backgroundColor: '#fafafa'
               }}
            />
          </Box>
        </>
      );
    
    // Proteins (substep 0)
    if (preferencesSubstep === 0) {
      return (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 500 }}>
                Protein Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Optional
              </Typography>
            </Box>
            <Button 
              variant="text" 
              color="primary" 
              onClick={onNext}
              sx={{ textTransform: 'none' }}
            >
              Skip
            </Button>
          </Box>
          <ProteinPreference 
            selectedProteins={safeFormData.likedProteins} 
            setSelectedProteins={v => handleChange('likedProteins', v)} 
            title="Proteins your cat likes"
          />
          
          {renderExplorationCheckbox(
            'exploreNewProteins',
            "I'm open to other proteins"
          )}
          
                     <Box sx={{ mt: 2 }}>
             <ProteinPreference 
               selectedProteins={safeFormData.dislikedProteins} 
               setSelectedProteins={v => handleChange('dislikedProteins', v)} 
               title="Proteins your cat dislikes"
             />
           </Box>
        </>
      );
    }
    
    // Textures (substep 1)
    if (preferencesSubstep === 1) {
      return (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 500 }}>
                Texture Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Optional
              </Typography>
            </Box>
            <Button 
              variant="text" 
              color="primary" 
              onClick={onNext}
              sx={{ textTransform: 'none' }}
            >
              Skip
            </Button>
          </Box>
          <TexturePreference 
            selectedTextures={safeFormData.likedTextures} 
            setSelectedTextures={v => handleChange('likedTextures', v)} 
            title="Textures your cat likes"
          />
          
          {renderExplorationCheckbox(
            'exploreNewTextures',
            "I'm open to other textures"
          )}
          
                     <Box sx={{ mt: 2 }}>
             <TexturePreference 
               selectedTextures={safeFormData.dislikedTextures} 
               setSelectedTextures={v => handleChange('dislikedTextures', v)} 
               title="Textures your cat dislikes"
             />
           </Box>
        </>
      );
    }
    
    // Brands (substep 2)
    if (preferencesSubstep === 2) {
      // Get unique brands from product options
      const brandOptions = Array.from(new Set((productOptions || []).map(p => p?.brand).filter(Boolean)))
        .sort()
        .map(b => ({ label: b, value: b }));
        
      return (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 500 }}>
                Brand Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Optional
              </Typography>
            </Box>
            <Button 
              variant="text" 
              color="primary" 
              onClick={onNext}
              sx={{ textTransform: 'none' }}
            >
              Skip
            </Button>
          </Box>
                                <Typography variant="h6" gutterBottom>
             Brands your cat likes
           </Typography>
           {loading ? (
             <Typography>Loading brands...</Typography>
           ) : (
             <Select 
               isMulti 
               options={brandOptions}
               value={safeFormData.preferredBrands.map(b => ({ label: b, value: b }))} 
               onChange={opts => handleChange('preferredBrands', (opts || []).map(o => o.value))} 
               placeholder="Select brands your cat likes" 
               className="brand-select" 
               isClearable
             />
           )}
           
           {renderExplorationCheckbox(
             'exploreNewBrands',
             "I'm open to other brands"
           )}
           
           <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
             Brands your cat dislikes
           </Typography>
           {loading ? (
             <Typography>Loading brands...</Typography>
           ) : (
             <Select 
               isMulti 
               options={brandOptions}
               value={safeFormData.dislikedBrands.map(b => ({ label: b, value: b }))} 
               onChange={opts => handleChange('dislikedBrands', (opts || []).map(o => o.value))} 
               placeholder="Select brands your cat dislikes" 
               className="brand-select" 
               isClearable
             />
           )}
        </>
      );
    }
    
    // Products (substep 3)
    // Create product options from the fetched data
    const productSelectOptions = (productOptions || [])
      .filter(p => p && p.id && p.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(p => ({
        label: `${p.brand || ''} - ${p.line || ''} - ${p.name}`,
        value: p.id
      }));
    
    // Create selected product values for the liked products
    const likedProductValues = safeFormData.likedProducts
      .map(id => {
        const product = productOptions.find(p => p && p.id === id);
        return product ? {
          label: `${product.brand || ''} - ${product.line || ''} - ${product.name}`,
          value: product.id
        } : null;
      })
      .filter(Boolean);
    
    // Create selected product values for the disliked products
    const dislikedProductValues = safeFormData.dislikedProducts
      .map(id => {
        const product = productOptions.find(p => p && p.id === id);
        return product ? {
          label: `${product.brand || ''} - ${product.line || ''} - ${product.name}`,
          value: product.id
        } : null;
      })
      .filter(Boolean);
    
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
                          <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 500 }}>
                Product Preferences
              </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Optional
            </Typography>
          </Box>
          <Button 
            variant="text" 
            color="primary" 
            onClick={onNext}
            sx={{ textTransform: 'none' }}
          >
            Skip
          </Button>
        </Box>
                 <Typography variant="h6" gutterBottom>
           Select products your cat likes
         </Typography>
        {loading ? (
          <Typography>Loading products...</Typography>
        ) : (
          <Select 
            isMulti 
            options={productSelectOptions}
            value={likedProductValues}
            onChange={opts => handleChange('likedProducts', (opts || []).map(o => o.value))} 
            placeholder="Select products your cat likes" 
            className="product-select" 
            isClearable
          />
        )}
                 <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
           Select products your cat dislikes
         </Typography>
        {loading ? (
          <Typography>Loading products...</Typography>
        ) : (
          <Select 
            isMulti 
            options={productSelectOptions}
            value={dislikedProductValues}
            onChange={opts => handleChange('dislikedProducts', (opts || []).map(o => o.value))} 
            placeholder="Select products your cat dislikes" 
            className="product-select" 
            isClearable
          />
        )}
      </>
    );
  };
  
  export default Preferences;